import {
    ClipboardListIcon,
    ExternalLinkIcon,
    CheckIcon,
    XIcon,
} from "lucide-react";
import { Button } from "@component/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { UserSubmissionJob } from "@type/assessments";
import { parseFormat } from "@util/date";
import { cn } from "@util/tailwind";

interface FormTabContentProps {
    formDisplay: UserSubmissionJob | null;
    hideExternalLink?: boolean;
}

export default function FormTabContent({ formDisplay, hideExternalLink }: FormTabContentProps) {
    return (
        <div className="h-full space-y-4">
            <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                        <ClipboardListIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                        Form Response
                    </h4>
                    {!hideExternalLink && (
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={!formDisplay}
                            onClick={() => {
                                window.open(`/assess/${formDisplay?.assessment_id}/form`, '_blank');
                            }}
                            className="h-8 gap-2"
                        >
                            <ExternalLinkIcon className="h-4 w-4" />
                            View Form Details
                        </Button>
                    )}
                </div>
                <div className="space-y-2">
                    <div className="text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Form Name:</span>
                        <span className="text-gray-900 dark:text-gray-100 font-medium ml-2">
                            {formDisplay?.assessment_name || ""}
                        </span>
                    </div>
                    <div className="text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Submission Date:</span>
                        <span className="text-gray-900 dark:text-gray-100 font-medium ml-2">
                            {formDisplay?.submission_date ? parseFormat(formDisplay.submission_date, "D MMMM YYYY") : ""}
                        </span>
                    </div>
                </div>
            </div>

            <div>
                <div className="flex flex-row justify-between items-center mb-3">
                    <h2 className="text-base font-semibold">
                        Questions ({(formDisplay?.questions || []).length})
                    </h2>


                    {/* Total Form Score — only show when at least one question
                        actually carries points. "No points" means null /
                        undefined; a points value of 0 is still a valid scored
                        question and counts as having points. `!= null` matches
                        both null and undefined in one check. */}
                    {formDisplay && formDisplay.questions && formDisplay.questions.length > 0 ? (() => {
                        const hasPoints = formDisplay.questions.some(q => q.points != null);
                        // `score` is `number | null`; a legitimate score of 0
                        // must still render, so gate on null/undefined only
                        // (not falsiness — `!0` would wrongly hide it).
                        if (!hasPoints || formDisplay.score == null) return null;
                        const totalPoints = (formDisplay.questions || []).reduce((acc, q) => acc + (q.points || 0), 0);
                        const scoredPoints = (formDisplay.answers || []).reduce((acc, a) => acc + (a.point ?? 0), 0);
                        return (
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">Total Form Score:</span>
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                                    {scoredPoints}/{totalPoints} points
                                </span>
                            </div>
                        );
                    })() : null}
                </div>
                <div className="space-y-4">
                    {(formDisplay?.questions || []).map((question, index) => {
                        const matchedAnswer = (formDisplay?.answers || []).find(ans => ans.id === question.id);
                        const answer = matchedAnswer?.content || "";
                        return (
                            <div
                                key={question.id}
                                className="border border-gray-300 rounded-lg p-4"
                                style={{ border: "0.909091px solid #d1d5db" }}
                            >
                                {/* Question Header */}
                                <div
                                    className="flex items-center justify-between mb-4"
                                    style={{
                                        paddingTop: "8px",
                                        paddingBottom: "0px",
                                        paddingLeft: "16px",
                                        paddingRight: "16px",
                                    }}
                                >
                                    <div className="flex items-center gap-4">
                                        <div
                                            className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full text-sm font-medium"
                                            style={{ border: "1px solid #bfdbfe" }}
                                        >
                                            {index + 1}
                                        </div>
                                        <span className="text-sm font-semibold text-gray-700">
                                            {question.type}
                                        </span>
                                    </div>
                                    {/* Show points if question has points */}
                                    {question.points && question.points !== undefined && (
                                        <span className="text-sm font-semibold text-blue-600">
                                            {matchedAnswer?.point ?? 0}/{question.points} {question.points === 1 ? 'point' : 'points'}
                                        </span>
                                    )}
                                </div>
                                {/* Question Content */}
                                <div
                                    className="space-y-4"
                                    style={{ paddingTop: "0px" }}
                                >
                                    <div
                                        className="space-y-4"
                                        style={{
                                            paddingBottom: "8px",
                                            paddingRight: "16px",
                                            paddingLeft: "16px",
                                        }}
                                    >
                                        <div
                                            className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                                            style={{
                                                backgroundColor: "transparent",
                                                border: "0px solid rgb(228, 228, 231)",
                                                paddingTop: "8px",
                                                paddingBottom: "8px",
                                                paddingRight: "0px",
                                                paddingLeft: "0px",
                                            }}
                                        >
                                            <div
                                                className="text-sm text-gray-700"
                                                dangerouslySetInnerHTML={{ __html: question.content }}
                                            />
                                        </div>

                                        {/* Candidate Response */}
                                        <div className="mt-4">
                                            <label className="text-sm font-semibold text-gray-700 block mb-2">
                                                Candidate Response:
                                            </label>
                                            {question.type === "Rating Scale" ? (
                                                <div className="space-y-4">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <span className="text-sm text-gray-600 dark:text-gray-400 text-right mr-2">
                                                            {question.min_label}
                                                        </span>
                                                        {Array.from({ length: (question.max_rate || 5) - (question.min_rate || 1) + 1 }, (_, i) => (question.min_rate || 1) + i).map((value) => (
                                                            <button
                                                                key={value}
                                                                type="button"
                                                                disabled
                                                                className={cn(
                                                                    "w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-medium transition-all",
                                                                    value === parseInt(answer)
                                                                        ? "bg-blue-600 border-blue-600 text-white"
                                                                        : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                                                                )}
                                                            >
                                                                {value}
                                                            </button>
                                                        ))}
                                                        <span className="text-sm text-gray-600 dark:text-gray-400 text-left ml-2">
                                                            {question.max_label}
                                                        </span>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                                            Selected: {answer}
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : question.type === "Multiple Choice" ? (
                                                <Select
                                                    value={answer || undefined}
                                                    disabled
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue placeholder="Select an option" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {(question.options || []).map((option, idx) => (
                                                            <SelectItem key={idx} value={option}>
                                                                {option}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <div className="bg-white border border-gray-200 rounded-md py-1.5 px-2 min-h-[36px]">
                                                    {answer ? (
                                                        <p className="text-sm text-gray-900">
                                                            {answer}
                                                        </p>
                                                    ) : (
                                                        <p className="text-sm text-gray-400 italic">
                                                            No response provided
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Short Answer Explanation */}
                                        {question.type === "Short Answer" && question.correct_answers && question.correct_answers.length > 0 && (
                                            <div className="mt-4">
                                                <div className="space-y-2">
                                                    <p className="text-sm text-gray-900">
                                                        {matchedAnswer?.summary || "No summary provided by the candidate."}
                                                    </p>
                                                    <div className="space-y-1.5">
                                                        <p className="text-sm text-gray-600">Expected answers:</p>
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            {question.correct_answers.map((keyword, idx) => (
                                                                <span
                                                                    key={idx}
                                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium"
                                                                >
                                                                    {keyword}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Multiple Choice Explanation */}
                                        {question.type === "Multiple Choice" && question.options && question.option_correct_index !== undefined && question.option_correct_index !== null && (
                                            <div className="mt-4">
                                                {answer === question.options[question.option_correct_index] ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                                            <CheckIcon className="w-3 h-3 text-white" />
                                                        </div>
                                                        <p className="text-sm text-gray-700">
                                                            Correct answer selected.
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                                                            <XIcon className="w-3 h-3 text-white" />
                                                        </div>
                                                        <p className="text-sm text-gray-700">
                                                            <span className="font-semibold">Incorrect answer.</span> The correct answer is: &ldquo;{question.options[question.option_correct_index]}&rdquo;
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Rating Scale Explanation */}
                                        {question.type === "Rating Scale" && question.min_rate !== undefined && question.max_rate !== undefined && (
                                            <div className="mt-4">
                                                <p className="text-sm text-gray-700">
                                                    The candidate provided a rating of {answer} out of {question.max_rate},{" "}
                                                    {Number(answer) >= (question.max_rate * 0.8)
                                                        ? "indicating a highly positive response."
                                                        : Number(answer) >= (question.max_rate * 0.6)
                                                            ? "indicating a moderately positive response."
                                                            : Number(answer) >= (question.max_rate * 0.4)
                                                                ? "indicating a negative response."
                                                                : "indicating a less favorable response."}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
